import { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ConnectionsList from "./connectionsList";

// Modal genérico de carga de credenciales de paquetería. Mismo llenado que los
// marketplaces (client_id + secret, sin OAuth). `carrier` = { name, partner, logoUrl }.
const LogisticsModal = ({ open, onClose, onSave, connections = [], onDelete, carrier }) => {
  const theme = useTheme();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const name = carrier?.name || "Paquetería";

  const handleSave = async () => {
    if (!clientId || !clientSecret) return;
    setLoading(true);
    setErrorMessage("");
    try {
      await onSave?.({ clientId, clientSecret });
      handleClose();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.response?.data?.message;
      setErrorMessage(apiMessage || `No se pudo conectar con ${name}.`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClientId("");
    setClientSecret("");
    setShowSecret(false);
    setErrorMessage("");
    onClose();
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: theme.palette.background.paper,
      "& fieldset": { borderColor: theme.palette.divider },
      "&:hover fieldset": { borderColor: theme.palette.text.secondary },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "1.5px",
      },
      "& input": { color: theme.palette.text.primary, fontSize: "0.88rem" },
      "& input::placeholder": { color: theme.palette.text.disabled },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      "&.Mui-focused": { color: theme.palette.primary.main },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: theme.palette.background.default,
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinkIcon sx={{ color: theme.palette.text.secondary, fontSize: 15 }} />
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              {name.toUpperCase()}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.text.primary },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 3 }}>
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {carrier?.logoUrl ? (
                <Box
                  component="img"
                  src={carrier.logoUrl}
                  alt={name}
                  sx={{ width: 32, height: 32, objectFit: "contain" }}
                />
              ) : null}
            </Box>
            <Box>
              <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: "0.95rem" }}>
                {name}
              </Typography>
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.78rem" }}>
                Conectá {name} con las credenciales de tu cuenta de la paquetería
              </Typography>
            </Box>
          </Box>

          {/* Existing connections */}
          <ConnectionsList connections={connections} onDelete={onDelete} />

          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "14px",
              p: 2.5,
            }}
          >
            <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: "0.9rem", mb: 2 }}>
              {connections.length > 0 ? `Agregar otra cuenta de ${name}` : `Credenciales de ${name}`}
            </Typography>

            <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.82rem", mb: 2 }}>
              Ingresá el{" "}
              <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                Client ID
              </Box>{" "}
              y el{" "}
              <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                Client Secret
              </Box>{" "}
              que te dio {name} para operar por API.
            </Typography>

            {errorMessage ? (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
                {errorMessage}
              </Alert>
            ) : null}

            <Alert
              icon={false}
              sx={{
                backgroundColor: alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                borderRadius: "10px",
                mb: 2.5,
                py: 1,
                "& .MuiAlert-message": {
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                },
              }}
            >
              🔒{" "}
              <Box component="span" sx={{ color: theme.palette.success.main, fontWeight: 700 }}>
                Seguridad:
              </Box>{" "}
              tus credenciales se cifran y solo se usan del lado server para cotizar y despachar tus envíos.
            </Alert>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="CLIENT ID"
                placeholder="Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                fullWidth
                size="small"
                sx={inputSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="CLIENT SECRET"
                placeholder="Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                type={showSecret ? "text" : "password"}
                fullWidth
                size="small"
                sx={inputSx}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowSecret(!showSecret)}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showSecret ? (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !clientId || !clientSecret}
                disableElevation
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  px: 2.5,
                  "&:hover": { backgroundColor: theme.palette.primary.dark },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                Guardar credenciales
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  px: 2.5,
                  "&:hover": {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LogisticsModal;
